<template>
  <component
    :is="component"
    v-if="exists"
    v-bind="$attrs"
    :class="classes"
    v-on="$listeners"
  />
</template>

<script>
import classes from '~~/common/vue/mixins/classes';

const large = {
  'check-circle': 'check-circle',
  'coins': 'coins',
  'heart': 'heart',
  'touch-show': 'touch-show',
  'search': 'search',
  'tree': 'tree',
  'tree-color': 'tree-color',
};

const medium = {
  'arrow-down': 'arrow-down',
  'arrow-left': 'arrow-left',
  'arrow-right': 'arrow-right',
  'atlas': 'atlas',
  'globe': 'globe',
  'center-position': 'center-position',
  'check-circle': 'check-circle',
  'check-circle-outline': 'check-circle-outline',
  'check-circle-transparent': 'check-circle-transparent',
  'check': 'check',
  'chevron-left': 'chevron-left',
  'chevron-mini-down': 'chevron-mini-down',
  'chevron-mini-up': 'chevron-mini-up',
  'chevron-right': 'chevron-right',
  'climate-pledge-rating-a': 'climate-pledge-rating-a',
  'climate-pledge-rating-b': 'climate-pledge-rating-b',
  'climate-pledge-rating-c': 'climate-pledge-rating-c',
  'climate-pledge-rating-d': 'climate-pledge-rating-d',
  'climate-pledge-rating-f': 'climate-pledge-rating-f',
  'close': 'close',
  'exclamation-circle': 'exclamation-circle',
  'ellipsis': 'ellipsis',
  'feedback': 'feedback',
  'hamburger': 'hamburger',
  'logo-compact': 'logo-compact',
  'minus': 'minus',
  'plus': 'plus',
  'play-fill': 'play-fill',
  'question-circle': 'question-circle',
  'search': 'search',
  'shopping': 'shopping',
  'green-leaf': 'green-leaf',
  'spinner': 'spinner',
  'tree': 'tree',
  'tree-color': 'tree-color',
  'whats-around': 'whats-around',
  'fossil-fuel': 'fossil-fuel',
  'retry': 'retry',
  'blog': 'social/blog',
  'facebook': 'social/facebook',
  'instagram': 'social/instagram',
  'linkedin': 'social/linkedin',
  'tiktok': 'social/tiktok',
  'twitter': 'social/twitter',
  'youtube': 'social/youtube',
  'source-amazon': 'external/source-amazon',
  'source-bing': 'external/source-bing',
  'source-ecosia': 'external/source-ecosia',
  'source-google': 'external/source-google',
  'source-google-maps': 'external/source-google-maps',
  'source-treeday': 'external/source-treeday',
  'source-tripadvisor': 'external/source-tripadvisor',
  'source-youtube': 'external/source-youtube',
  'source-wikipedia': 'external/source-wikipedia',
};

const small = {
  'arrow-left': 'arrow-left',
  'arrow-right': 'arrow-right',
  'atlas': 'atlas',
  'bell': 'bell',
  'check': 'check',
  'center-position': 'center-position',
  'check-circle': 'check-circle',
  'check-circle-solid': 'check-circle-solid',
  'check-circle-transparent': 'check-circle-transparent',
  'chevron-down': 'chevron-down',
  'chevron-mini-down': 'chevron-mini-down',
  'chevron-left': 'chevron-left',
  'chevron-right': 'chevron-right',
  'chevron-up': 'chevron-up',
  'chevron-mini-up': 'chevron-mini-up',
  'close': 'close',
  'exclamation-circle': 'exclamation-circle',
  'feedback': 'feedback',
  'globe': 'globe',
  'green-leaf': 'green-leaf',
  'hamburger': 'hamburger',
  'highlight-star': 'highlight-star',
  'info-circle': 'info-circle',
  'leaf': 'leaf',
  'map-pin': 'map-pin',
  'minus': 'minus',
  'phone': 'phone',
  'play': 'play',
  'plus': 'plus',
  'plus-one': 'plus-one',
  'question-circle': 'question-circle',
  'reviews': 'reviews',
  'search': 'search',
  'images': 'images',
  'maps': 'maps',
  'more': 'more',
  'news': 'news',
  'videos': 'videos',
  'retry': 'retry',
  'star-fill': 'star-fill',
  'star-half': 'star-half',
  'star': 'star',
  'tab-open': 'tab-open',
  'time': 'time',
  'tree-color': 'tree-color',
  'tripadvisor-rating-fill': 'ta-rating-fill',
  'tripadvisor-rating-half': 'ta-rating-half',
  'tripadvisor-rating': 'ta-rating',
  'search-mini': 'search-mini',
  'samaritians': 'external/samaritians',
  'dargebotene-hand': 'external/dargebotene-hand',
  'telefon-seelsorge-at': 'external/telefon-seelsorge-at',
  'telefon-seelsorge-de': 'external/telefon-seelsorge-de',
  'source-apple': 'external/source-apple',
  'source-google-maps': 'external/source-google-maps',
  'source-pj': 'external/source-pj',
  'source-tripadvisor': 'external/source-tripadvisor',
  'shopping': 'shopping',
  'whats-around': 'whats-around',
  'facebook': 'social/facebook',
  'instagram': 'social/instagram',
  'linkedin': 'social/linkedin',
  'tiktok': 'social/tiktok',
  'twitter': 'social/twitter',
  'youtube': 'social/youtube',
  'logo-compact': 'logo-compact',
  // alias
  'web': 'search-mini',
};

const sizeMap = {
  s: small,
  m: medium,
  l: large,
};

export const sizes = Object.keys(sizeMap);

export default {
  name: 'EIcon',
  mixins: [classes],
  props: {
    /**
     * available icons, see examples
     */
    icon: {
      type: String,
      required: true,
      cssClass: true,
    },
    /**
     * `s, m, l`
     */
    size: {
      type: String,
      default: 'm',
      validator: (value) => sizes.includes(value),
      cssClass: true,
    },
  },
  computed: {
    partialPath() {
      return sizeMap[this.size][this.icon];
    },
    component() {
      // IMPORTANT! do not remove this line
      // accessing the reactive properties of the component VM here
      // ensures that the computed is updated when they change.
      // Calling `this[property]` inside the import statement does not count,
      // because when Vue tries to figure out the "dependencies" of this computed,
      // it will not execute the returned function
      const { partialPath, size } = this;
      return () => import(
        /* webpackMode: "lazy-once", webpackChunkName: "icons" */
        `~~/common/vue/components/icons/${size}/${partialPath}.svg`
      );
    },
    exists() {
      const exists = !!this.partialPath;
      if (!exists) {
        // eslint-disable-next-line no-console
        console.error(`Icon ${this.icon} of size ${this.size} does not exist.`);
      }
      return exists;
    },
  },
};
</script>

<style lang="scss" scoped>
.icon {
  vertical-align: middle;

  &.icon--icon-check-circle.icon--size-l path {
    animation: e-svg-stroke 0.5s ease-out forwards;
    stroke-dasharray: 29;
    stroke-dashoffset: 29;
  }

  &--icon-spinner {
    animation: e-spin 0.5s linear both infinite;
  }
}
</style>
